import type { FC } from 'react';

import BarChart from '@/common-components/charts/BarChart';
import Pill from '@/common-components/Pill';
import type { PeakHour } from '@/types/stats.types';

interface PeakHoursCardProps {
  hours: PeakHour[];
}

/** 0 → "12AM", 13 → "1PM" — the label format the prototype's axis uses. */
const hourLabel = (hour: number): string => {
  if (hour === 0) return '12AM';
  if (hour === 12) return '12PM';
  return hour < 12 ? `${hour}AM` : `${hour - 12}PM`;
};

const PeakHoursCard: FC<PeakHoursCardProps> = ({ hours }) => {
  const busiest = hours.reduce(
    (best, hour) => (hour.bookings > best.bookings ? hour : best),
    hours[0] ?? { hour: 0, bookings: 0 },
  );
  const hasData = hours.some((hour) => hour.bookings > 0);

  return (
    <section className="rounded-2xl border border-[var(--color-divider)] bg-white p-6 shadow-[var(--shadow-card)]">
      <header className="mb-2 flex items-center justify-between">
        <h2 className="text-[15px] font-extrabold text-[var(--color-ink)]">Peak Usage Hours</h2>
        <Pill tone="info">IST</Pill>
      </header>

      {!hasData ? (
        <p className="py-10 text-center text-[13px] text-[var(--color-ink-muted)]">
          Not enough bookings to show a pattern yet.
        </p>
      ) : (
        <BarChart
          bars={hours.map((hour) => ({ label: hourLabel(hour.hour), value: hour.bookings }))}
          peakLabel={`Peak: ${hourLabel(busiest.hour)}`}
          labelEvery={4}
        />
      )}
    </section>
  );
};

export default PeakHoursCard;
